import { z } from "zod";
import { normalizeSymbol } from "./instrument";
import { formatPrice, PRICE_SCALE, type Price, price } from "./money";
import { TIMEFRAMES, type Timeframe } from "./quote";
import type { RecordedBar } from "./recording";

/**
 * Years of daily bars for a whole universe: the artifact a backtest reads.
 *
 * Beside `recording.ts` and deliberately not the same type. A *recording* is
 * one intraday session with a modeled spread, fetched to replay a day; a
 * *history* is one bar per session across years, fetched so a strategy can ask
 * what a price has DONE. Merging them would put a `half_spread_bps` on a series
 * that never fills an order and a `date` on a file spanning eight hundred of
 * them.
 *
 * What they do share is the property that matters: **the bytes are canonical**.
 * `serializeHistory` sorts symbols, sorts bars and writes keys in a fixed
 * order, so the same logical history produces the same string on any machine.
 * A serialization that varied would make "identical scores twice" a claim about
 * JSON key ordering rather than about the strategy.
 *
 * `t` is epoch milliseconds at the bar's **open**, exactly as `RecordedBar`
 * means it — one meaning of `t` in this repository, and the reader stamps
 * `asOf = t + TIMEFRAME_MS` because that is when a bar became knowable. Two
 * meanings of `t` is the defect `N99` found in three providers at once.
 */

/**
 * The format WRITTEN today. Both are still READ — see `HISTORY_FORMATS`.
 *
 * `@2` adds `members`: who was in the universe, and under which ticker, on
 * which days. `@1` had no way to express that and did not need one, because a
 * flat present-day list has no membership to describe. The bump is what lets a
 * reader tell a point-in-time artifact from a static one **by looking at it**,
 * which is what `static_universe` needs — the alternative is inferring intent
 * from an absent field, and an absent field means "old file" and "no changes"
 * at the same time.
 */
export const HISTORY_FORMAT = "atc-history/2";

/**
 * Every format a reader accepts, newest first.
 *
 * `bars/universe@1.json` is sitting in two R2 buckets right now at `@1`, and it
 * is the corpus the `M5/I10` regression gate replays. Refusing it on a format
 * bump would break a green path to add a field it does not use.
 */
export const HISTORY_FORMATS = ["atc-history/2", "atc-history/1"] as const;
export type HistoryFormat = (typeof HISTORY_FORMATS)[number];

/**
 * One issuer's membership, as the artifact records it.
 *
 * The listings are `@atc/domain`'s own `Listing`s, copied into the file rather
 * than referenced, for the reason `adjusted` and `halfSpreadBps` are also in
 * the file: a reader must be able to tell what it is holding without consulting
 * the code that wrote it. A backtest run six months from now against a stored
 * artifact reads the membership that produced it, not today's table.
 */
export type HistoryMember = {
  symbol: string;
  listings: readonly { symbol: string; from: string; until?: string }[];
  /**
   * Which venue's bars these are, and how far they sat from the consolidated
   * close where both existed. Provenance, written by the fetch — `ADR-0021`.
   */
  venue?: string;
  medianDivergenceBps?: number;
};

export type HistorySymbol = {
  symbol: string;
  bars: readonly RecordedBar[];
};

export type History = {
  format: HistoryFormat;
  /**
   * Which universe these bars are for — `universe@1`.
   *
   * In the file because changing the universe changes every score computed
   * against it, and a reader must be able to tell which one it is holding
   * without consulting the code that wrote it.
   */
  universe: string;
  /** Provenance only. Nothing branches on it — a reader never calls a vendor. */
  source: string;
  timeframe: Timeframe;
  /**
   * Whether corporate actions have been applied.
   *
   * **In the file, and never assumed.** An unadjusted series is usable if you
   * know it is unadjusted; a series that claims to be adjusted and is not is
   * how a 4:1 split becomes a -75% "return" inside a multi-month signal. The
   * flag travels with the data for the same reason `halfSpreadBps` does: a
   * model kept in the fetcher's memory is a model the reader cannot see.
   */
  adjusted: boolean;
  /** `YYYY-MM-DD`, the window requested. `end` is exclusive, as the vendor means it. */
  range: { start: string; end: string };
  /** Sorted by symbol. */
  symbols: readonly HistorySymbol[];
  /**
   * Who was a member when. Absent on `atc-history/1`, and that absence is a
   * fact rather than an omission: a `@1` artifact cannot support a
   * qualification and `static_universe` is what says so.
   */
  members?: readonly HistoryMember[];
};

// ---------------------------------------------------------------------------
// Wire schema
// ---------------------------------------------------------------------------

const decimalString = z
  .string()
  .regex(/^\d+(\.\d+)?$/, "must be a decimal string, not a JSON number")
  .refine((s) => /[1-9]/.test(s), "must be greater than zero");

const barSchema = z.object({
  t: z.number().int(),
  o: decimalString,
  h: decimalString,
  l: decimalString,
  c: decimalString,
  v: z.number().int().nonnegative(),
});

const historySymbolSchema = z.object({
  symbol: z.string().min(1).max(16),
  bars: z.array(barSchema),
});

const listingSchema = z.object({
  symbol: z.string().min(1).max(16),
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  until: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
});

const historyMemberSchema = z.object({
  symbol: z.string().min(1).max(16),
  listings: z.array(listingSchema).min(1),
  venue: z.string().min(1).max(16).optional(),
  median_divergence_bps: z.number().optional(),
});

const historySchema = z.object({
  format: z.enum(HISTORY_FORMATS),
  universe: z.string().min(1).max(64),
  source: z.string().min(1).max(64),
  timeframe: z.enum(TIMEFRAMES),
  adjusted: z.boolean(),
  range: z.object({
    start: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    end: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  }),
  symbols: z.array(historySymbolSchema),
  members: z.array(historyMemberSchema).optional(),
});

export type HistoryWire = z.infer<typeof historySchema>;

/** Validate and normalize. Throws with the offending field named. */
export function parseHistory(input: unknown): History {
  const wire = historySchema.parse(input);

  const symbols = wire.symbols
    .map((s) => ({
      symbol: normalizeSymbol(s.symbol),
      bars: [...s.bars]
        .sort((a, b) => a.t - b.t)
        .map((b) => ({
          t: b.t,
          open: price(b.o),
          high: price(b.h),
          low: price(b.l),
          close: price(b.c),
          volume: b.v,
        })),
    }))
    .sort((a, b) => (a.symbol < b.symbol ? -1 : a.symbol > b.symbol ? 1 : 0));

  const duplicate = symbols.find((s, i) => i > 0 && s.symbol === symbols[i - 1]?.symbol);
  if (duplicate) throw new TypeError(`history lists ${duplicate.symbol} twice`);

  return {
    format: wire.format,
    universe: wire.universe,
    source: wire.source,
    timeframe: wire.timeframe,
    adjusted: wire.adjusted,
    range: wire.range,
    symbols,
    ...(wire.members === undefined
      ? {}
      : {
          members: wire.members.map((m) => ({
            symbol: normalizeSymbol(m.symbol),
            listings: m.listings.map((l) => ({
              symbol: normalizeSymbol(l.symbol),
              from: l.from,
              ...(l.until === undefined ? {} : { until: l.until }),
            })),
            ...(m.venue === undefined ? {} : { venue: m.venue }),
            ...(m.median_divergence_bps === undefined
              ? {}
              : { medianDivergenceBps: m.median_divergence_bps }),
          })),
        }),
  };
}

/**
 * The canonical bytes. Key order is written out literally rather than left to
 * object-literal order, because this output is compared byte for byte.
 *
 * Not pretty-printed, unlike a recording: a universe over two years is ~27,000
 * bars, and two spaces of indent per line is several megabytes of whitespace in
 * an object the Worker fetches.
 */
export function serializeHistory(history: History): string {
  const symbols = [...history.symbols]
    .sort((a, b) => (a.symbol < b.symbol ? -1 : a.symbol > b.symbol ? 1 : 0))
    .map((s) => ({
      symbol: s.symbol,
      bars: [...s.bars]
        .sort((a, b) => a.t - b.t)
        .map((b) => ({
          t: b.t,
          o: formatPrice(b.open),
          h: formatPrice(b.high),
          l: formatPrice(b.low),
          c: formatPrice(b.close),
          v: b.volume,
        })),
    }));

  return `${JSON.stringify({
    format: history.format,
    universe: history.universe,
    source: history.source,
    timeframe: history.timeframe,
    adjusted: history.adjusted,
    range: { start: history.range.start, end: history.range.end },
    symbols,
    // Absent stays absent rather than becoming `[]`. An empty list is a
    // universe with no members, which is a different and much stranger claim
    // than a file that predates the field — and `@1` bytes must round-trip
    // unchanged or the R2 artifact stops matching itself.
    ...(history.members === undefined
      ? {}
      : {
          members: [...history.members]
            .sort((a, b) => (a.symbol < b.symbol ? -1 : a.symbol > b.symbol ? 1 : 0))
            .map((m) => ({
              symbol: m.symbol,
              listings: m.listings.map((l) => ({
                symbol: l.symbol,
                from: l.from,
                ...(l.until === undefined ? {} : { until: l.until }),
              })),
              ...(m.venue === undefined ? {} : { venue: m.venue }),
              ...(m.medianDivergenceBps === undefined
                ? {}
                : { median_divergence_bps: m.medianDivergenceBps }),
            })),
        }),
  })}\n`;
}

// ---------------------------------------------------------------------------
// Corporate actions
// ---------------------------------------------------------------------------

/**
 * A corporate action in OUR vocabulary, not a vendor's.
 *
 * Deliberately narrow: a split is the only kind that makes an unadjusted series
 * *wrong* rather than merely incomplete. A dividend makes a total-return figure
 * understate by the yield, which is a known bias a reader can reason about; a
 * split puts a -75% move in a series where nothing happened, and a multi-month
 * signal computed across it is not noisy but inverted.
 *
 * `ratio` is a decimal STRING for the reason every number in this repository
 * is: a 3:1 split as `0.3333333333333333` is a rounding error compounded across
 * eight hundred sessions.
 */
export type CorporateAction = {
  kind: "split";
  symbol: string;
  /** `YYYY-MM-DD`. The first session that trades at the NEW price. */
  exDate: string;
  /**
   * New shares per old share. A 4:1 forward split is `"4"`; a 1:10 reverse
   * split is `"0.1"`. Prices before the ex-date are DIVIDED by it.
   */
  ratio: string;
};

const RATIO_SCALE = 12n;
const RATIO_UNIT = 10n ** RATIO_SCALE;

function ratioAsScaled(action: CorporateAction): bigint {
  const match = /^(\d+)(?:\.(\d+))?$/.exec(action.ratio.trim());
  if (!match) {
    throw new TypeError(
      `${action.symbol} ${action.exDate}: ratio is not a decimal: ${action.ratio}`,
    );
  }
  const [, whole, fraction = ""] = match;
  const padded = fraction.padEnd(Number(RATIO_SCALE), "0").slice(0, Number(RATIO_SCALE));
  const scaled = BigInt(whole as string) * RATIO_UNIT + BigInt(padded === "" ? "0" : padded);
  if (scaled <= 0n) {
    throw new RangeError(
      `${action.symbol} ${action.exDate}: ratio must be positive, got ${action.ratio}`,
    );
  }
  return scaled;
}

/**
 * Divide a price by a scaled ratio, or throw rather than round.
 *
 * The same rule the Databento decoder holds the vendor to, applied to our own
 * arithmetic: six places are stored, and a division that would lose a digit is
 * a fact worth failing on. A 3:1 split of a price ending in an odd cent cannot
 * be represented, and a series quietly rounded at every bar is a series whose
 * returns are not the returns.
 */
function divideExactly(value: Price, scaledRatio: bigint, what: string): Price {
  const numerator = (value as bigint) * RATIO_UNIT;
  if (numerator % scaledRatio !== 0n) {
    throw new RangeError(
      `${what}: adjusting ${formatPrice(value)} by this ratio needs more than ${PRICE_SCALE} places`,
    );
  }
  const result = numerator / scaledRatio;
  if (result <= 0n) throw new RangeError(`${what}: adjustment produced a non-positive price`);
  return result as Price;
}

/**
 * Apply splits to a raw series, so what is stored is what a strategy may read.
 *
 * **Adjustment happens before storage, never at read time.** A reader that
 * adjusted would have to be handed the actions too, and every reader that
 * forgot would compute a signal across a cliff — the failure is silent and the
 * numbers merely look good. Storing the adjusted series means there is no raw
 * price in the system for anything to read by accident.
 *
 * Actions are applied in ex-date order and compose: a bar before two splits
 * carries both factors. Only bars strictly BEFORE an ex-date are scaled, which
 * is the definition of the ex-date — the first session trading at the new
 * price.
 *
 * Volume moves the other way, because shares multiply as prices divide. A
 * volume series that was not adjusted would show a 4x jump on the ex-date and
 * any liquidity filter reading it would see a stock suddenly become tradable.
 */
export function adjustForActions(
  bars: readonly RecordedBar[],
  actions: readonly CorporateAction[],
): readonly RecordedBar[] {
  if (actions.length === 0) return bars;

  const ordered = [...actions].sort((a, b) => (a.exDate < b.exDate ? -1 : 1));

  return bars.map((bar) => {
    const day = new Date(bar.t).toISOString().slice(0, 10);

    // Every action whose ex-date is still ahead of this bar applies to it.
    let scaled = RATIO_UNIT;
    for (const action of ordered) {
      if (day < action.exDate) scaled = (scaled * ratioAsScaled(action)) / RATIO_UNIT;
    }
    if (scaled === RATIO_UNIT) return bar;

    const what = `${day}`;
    return {
      t: bar.t,
      open: divideExactly(bar.open, scaled, what),
      high: divideExactly(bar.high, scaled, what),
      low: divideExactly(bar.low, scaled, what),
      close: divideExactly(bar.close, scaled, what),
      volume: Number((BigInt(bar.volume) * scaled) / RATIO_UNIT),
    };
  });
}

/**
 * A move so large it is almost certainly a corporate action nobody told us
 * about, not a price.
 *
 * **Expressed as a RATIO, symmetrically, and that is the correction.** The
 * first version was `1 ± 0.35`, read as "a 35% move" — which it is not. A ratio
 * of 1.35 is a 25.9% *drop*, and a ratio of 0.65 is a 53.8% *rise*: the same
 * number meant two different sensitivities depending on direction, and the
 * tighter of them was tight enough to be wrong.
 *
 * It was caught against the real 2.2 years rather than by a test. Intel fell
 * 26% on 2024-08-02 on its Q2 earnings — ratio 1.3524, a genuine price move —
 * and the old band flagged it as a split. Adjusting a series for that would
 * have flattened a real crash into nothing, which is worse than the unadjusted
 * series this exists to prevent: a strategy would have been scored on a market
 * where Intel's worst day did not happen.
 *
 * `1.6` sits in the gap between the two populations, which is wide because the
 * things either side of it are not close: the smallest split anybody performs
 * is 2:1 — ratio 2.0 — and the sharpest single-session moves in a universe of
 * this size are in the twenties and thirties. It admits any real move up to a
 * 37.5% drop or a 60% rise, and catches every split.
 */
export const GAP_RATIO_THRESHOLD = 1.6;

export type PriceGap = {
  /** The open of the bar the gap happened INTO, in epoch ms. */
  at: number;
  /**
   * `previous close / this close` — deliberately the same number
   * `CorporateAction.ratio` carries, so a 4:1 forward split reads as `4` and a
   * 1-for-10 reverse split reads as `0.1`.
   *
   * The point of a gap report is to name the action to go and look up. A figure
   * a reader has to invert first is one they will eventually invert wrongly.
   */
  ratio: number;
};

/**
 * Every overnight move this series cannot explain.
 *
 * **This is the verification that adjustment worked, not the mechanism.** A
 * test that only checked the adjusted series would pass against an adjustment
 * that did nothing, which is exactly the failure mode worth guarding: an
 * adjustment step that silently no-ops leaves a series that is wrong in the one
 * way nothing downstream can detect.
 *
 * A gap explained by an action we were told about is not reported, so a run
 * with a working corporate-actions feed is quiet and a run without one names
 * the symbol and the date to go and look up.
 */
export function unexplainedGaps(
  bars: readonly RecordedBar[],
  actions: readonly CorporateAction[],
): readonly PriceGap[] {
  const exDates = new Set(actions.map((a) => a.exDate));
  const gaps: PriceGap[] = [];

  for (let i = 1; i < bars.length; i++) {
    const previous = bars[i - 1] as RecordedBar;
    const current = bars[i] as RecordedBar;
    const day = new Date(current.t).toISOString().slice(0, 10);
    if (exDates.has(day)) continue;

    const ratio = Number(previous.close) / Number(current.close);
    if (ratio >= GAP_RATIO_THRESHOLD || ratio <= 1 / GAP_RATIO_THRESHOLD) {
      gaps.push({ at: current.t, ratio });
    }
  }

  return gaps;
}

/** Bars out of a decoded provider result, in the wire shape this file stores. */
export function toHistoryBars(
  bars: readonly {
    open: Price;
    high: Price;
    low: Price;
    close: Price;
    volume: number;
    asOf: Date;
  }[],
  spanMs: number,
): readonly RecordedBar[] {
  return bars
    .map((b) => ({
      // Back to the bar's OPEN, because `t` means the open everywhere in this
      // repository and a stamp is derived from it on read. See `N99`.
      t: b.asOf.getTime() - spanMs,
      open: b.open,
      high: b.high,
      low: b.low,
      close: b.close,
      volume: b.volume,
    }))
    .sort((a, b) => a.t - b.t);
}

/**
 * The fraction of the longest series a symbol must have to count as complete.
 *
 * Not `1`. A trading halt, a suspension or a listing a week into the window
 * costs sessions without making the series meaningless, and a rule demanding an
 * exact match would refuse a perfectly good universe on a suspension. `0.9` is
 * far above what any of those cost and far below what a ticker change does —
 * `SQ` had 26% of the window.
 */
export const COMPLETE_SERIES_FRACTION = 0.9;

export type ShortSeries = {
  symbol: string;
  bars: number;
  /** What the fullest symbol in the same fetch had. */
  expected: number;
};

/**
 * Every symbol whose history stops short of its peers'.
 *
 * **Measured against the other symbols rather than against a calendar**, which
 * is the only version that needs no exchange-holiday table and cannot be wrong
 * about one. Fifty-two symbols on the same venue over the same window see the
 * same sessions; a symbol that saw a quarter of them did not have a quiet year,
 * it stopped existing under that name.
 *
 * That is what `SQ` did — Block renamed to `XYZ` in January 2025, and the
 * series ends on 2025-01-17 with prices that are all perfectly correct. Nothing
 * in the data is wrong, which is exactly why nothing else catches it.
 */
export function shortSeries(
  symbols: readonly { symbol: string; bars: readonly RecordedBar[] }[],
  fraction = COMPLETE_SERIES_FRACTION,
): readonly ShortSeries[] {
  if (symbols.length === 0) return [];
  const expected = Math.max(...symbols.map((s) => s.bars.length));

  return symbols
    .filter((s) => s.bars.length < expected * fraction)
    .map((s) => ({ symbol: s.symbol, bars: s.bars.length, expected }));
}

/**
 * Every symbol whose history stops short of what its MEMBERSHIP predicts.
 *
 * **The point-in-time replacement for `shortSeries`, and the narrowing is
 * deliberate.** `shortSeries` compares every symbol against the fullest one in
 * the fetch, which is exactly right for a flat present-day universe: fifty-two
 * names on the same venue over the same window see the same sessions, so a
 * quarter of them is a rename nobody noticed. Applied to a point-in-time
 * universe it is wrong in the one direction that matters — Coinbase listed in
 * April 2021 and has 65% of the sessions the window holds, which is not a
 * defect but the entire fact the universe exists to record, and refusing it
 * would make a point-in-time fetch impossible.
 *
 * So the comparison moves from "the fullest series" to "the sessions this
 * member was actually listed for", and it stays just as strict inside that
 * window. What now covers the gap `shortSeries` used to: a rename is no longer
 * detected by a short series at all, because a rename is *declared* in the
 * table and fetched as two windows — and `assertWellFormed` refuses a table
 * whose listings are not contiguous. A ticker change nobody wrote down still
 * produces a short series, and this still catches it.
 *
 * **The session calendar comes from the data**, as before: the union of every
 * bar timestamp seen in the fetch. No exchange-holiday table, and therefore no
 * exchange-holiday table to be wrong.
 */
export function membershipShortSeries(
  symbols: readonly { symbol: string; bars: readonly RecordedBar[] }[],
  members: readonly {
    symbol: string;
    listings: readonly { symbol: string; from: string; until?: string }[];
  }[],
  fraction = COMPLETE_SERIES_FRACTION,
): readonly ShortSeries[] {
  const sessions = [...new Set(symbols.flatMap((s) => s.bars.map((b) => b.t)))].sort(
    (a, b) => a - b,
  );
  if (sessions.length === 0) return [];

  const day = (t: number) => new Date(t).toISOString().slice(0, 10);
  const bySymbol = new Map(symbols.map((s) => [s.symbol, s.bars.length]));
  const out: ShortSeries[] = [];

  for (const member of members) {
    const bars = bySymbol.get(member.symbol);
    if (bars === undefined) continue;
    // Sessions inside ANY of this member's listing windows. A renamed issuer
    // is one member with two windows, so its expectation is the sum and not
    // the longer of the two.
    const expected = sessions.filter((t) => {
      const d = day(t);
      return member.listings.some((l) => d >= l.from && (l.until === undefined || d < l.until));
    }).length;
    if (expected > 0 && bars < expected * fraction) {
      out.push({ symbol: member.symbol, bars, expected });
    }
  }
  return out;
}

/**
 * The largest relative price error a rounded adjustment may introduce.
 *
 * One part in ten million. Rounding to `PRICE_SCALE` places moves a price by at
 * most half of one unit in the last place, so for any price above about five
 * cents the true error is orders of magnitude under this. The bound is not
 * calibrated to that slack — it is set where it will still catch the thing worth
 * catching, which is a **wrong ratio**: adjusting by 3 what should have been
 * adjusted by 4 moves a price by 33%, not by a millionth.
 */
export const MAX_ADJUSTMENT_ERROR = 1e-7;

/**
 * `adjustForActions`, but rounding to the nearest representable price — and
 * proving the rounding was harmless rather than assuming it.
 *
 * **Why this exists at all, and why `adjustForActions` is not changed.** That
 * function refuses rather than rounds, on the stated ground that "a series
 * quietly rounded at every bar is a series whose returns are not the returns".
 * The reasoning is right and the rule holds for `universe@1`, whose only action
 * is a 10:1 split. Over `universe@2`'s six years it becomes unsatisfiable:
 * **a 3:1 split cannot be represented exactly at any fixed decimal scale**,
 * because one third is not a terminating decimal. Measured across the ten
 * corporate actions in that window, eight adjust exactly — ratios of 20, 10, 4
 * and 0.125 — and the two 3:1 splits, TSLA in 2022 and WMT in 2024, cannot.
 *
 * The choice is therefore not "exact or rounded"; it is "rounded and checked,
 * or no six-year history at all". So the operative word in the original rule is
 * **quietly**, and this removes it: every adjusted price is compared back
 * against the exact rational value and the bar is refused if it moved by more
 * than `MAX_ADJUSTMENT_ERROR`. A wrong ratio fails loudly; a third of a cent in
 * the seventh decimal place does not.
 *
 * `adjustForActions` keeps its refusal and keeps its callers. Two functions,
 * because they make two different promises, and a caller should have to say
 * which one it wants.
 */
export function adjustForActionsToNearest(
  bars: readonly RecordedBar[],
  actions: readonly CorporateAction[],
): readonly RecordedBar[] {
  if (actions.length === 0) return bars;
  const ordered = [...actions].sort((a, b) => (a.exDate < b.exDate ? -1 : 1));

  return bars.map((bar) => {
    const day = new Date(bar.t).toISOString().slice(0, 10);

    let scaled = RATIO_UNIT;
    for (const action of ordered) {
      if (day < action.exDate) scaled = (scaled * ratioAsScaled(action)) / RATIO_UNIT;
    }
    if (scaled === RATIO_UNIT) return bar;

    const at = `${day}`;
    return {
      t: bar.t,
      open: divideToNearest(bar.open, scaled, `${at} open`),
      high: divideToNearest(bar.high, scaled, `${at} high`),
      low: divideToNearest(bar.low, scaled, `${at} low`),
      close: divideToNearest(bar.close, scaled, `${at} close`),
      // Volume scales the other way and is an integer count, so it truncates
      // exactly as `adjustForActions` truncates it. A share count is not a
      // price and has no fractional part to preserve.
      volume: Number((BigInt(bar.volume) * scaled) / RATIO_UNIT),
    };
  });
}

/**
 * Divide, round half away from zero, and refuse if the rounding mattered.
 *
 * The comparison is done on integers — `result * ratio` against
 * `value * RATIO_UNIT` — so the check itself never introduces the floating
 * point error it is looking for.
 */
function divideToNearest(value: Price, scaledRatio: bigint, what: string): Price {
  const numerator = (value as bigint) * RATIO_UNIT;
  const quotient = numerator / scaledRatio;
  const remainder = numerator % scaledRatio;
  // Round half away from zero. Every price here is positive, and the branch is
  // written for the sign anyway so it cannot become wrong if that changes.
  const rounded = remainder * 2n >= scaledRatio ? quotient + 1n : quotient;

  if (rounded <= 0n) {
    throw new RangeError(`${what}: adjusting ${formatPrice(value)} by this ratio rounds to zero`);
  }

  // What the rounded price implies the ORIGINAL was, back through the same
  // ratio. If that is not the original to within the bound, the ratio is wrong
  // rather than the arithmetic being lossy.
  const impliedNumerator = rounded * scaledRatio;
  const drift =
    impliedNumerator > numerator ? impliedNumerator - numerator : numerator - impliedNumerator;
  if (Number(drift) / Number(numerator) > MAX_ADJUSTMENT_ERROR) {
    throw new RangeError(
      `${what}: adjusting ${formatPrice(value)} by this ratio moves it by ` +
        `${((Number(drift) / Number(numerator)) * 100).toFixed(6)}%, more than rounding explains`,
    );
  }
  return rounded as Price;
}
